import {
  DocumentOutputSection,
  DocumentOutputSubsection,
  DocumentOutputField,
  CreateDocumentOutputSection,
  CreateDocumentOutputSubsection,
  CreateDocumentOutputField,
  UpdateDocumentOutputSection,
  UpdateDocumentOutputSubsection,
  UpdateDocumentOutputField,
} from "@/models/DocumentOutputFormat";
import { apiRequest } from "./apiUtils";
import { config } from "@/config";

const SUPABASE_URL = config.SUPABASE_URL;

export const documentOutputFormatApi = {
  /**
   * Get all sections with their subsections and fields for a document
   */
  getSections: async (documentId: number): Promise<DocumentOutputSection[]> => {
    const response = await apiRequest<{ data: DocumentOutputSection[] }>(
      `${SUPABASE_URL}/functions/v1/document-output-formats/${documentId}`
    );
    return response.data;
  },

  /**
   * Create a new section for a document
   */
  createSection: async (
    documentId: number,
    sectionData: CreateDocumentOutputSection
  ): Promise<DocumentOutputSection> => {
    const response = await apiRequest<{ data: DocumentOutputSection }>(
      `${SUPABASE_URL}/functions/v1/document-output-formats/${documentId}`,
      {
        method: "POST",
        body: sectionData,
      }
    );
    return response.data;
  },

  /**
   * Update a section
   */
  updateSection: async (
    sectionId: number,
    updateData: UpdateDocumentOutputSection
  ): Promise<DocumentOutputSection> => {
    const response = await apiRequest<{ data: DocumentOutputSection }>(
      `${SUPABASE_URL}/functions/v1/document-output-formats/sections/${sectionId}`,
      {
        method: "PUT",
        body: updateData,
      }
    );
    return response.data;
  },

  /**
   * Delete a section
   */
  deleteSection: async (sectionId: number): Promise<void> => {
    await apiRequest<{ success: boolean }>(
      `${SUPABASE_URL}/functions/v1/document-output-formats/sections/${sectionId}`,
      {
        method: "DELETE",
      }
    );
  },

  /**
   * Create a new subsection for a section
   */
  createSubsection: async (
    sectionId: number,
    subsectionData: CreateDocumentOutputSubsection
  ): Promise<DocumentOutputSubsection> => {
    const response = await apiRequest<{ data: DocumentOutputSubsection }>(
      `${SUPABASE_URL}/functions/v1/document-output-formats/sections/${sectionId}`,
      {
        method: "POST",
        body: subsectionData,
      }
    );
    return response.data;
  },

  /**
   * Update a subsection
   */
  updateSubsection: async (
    subsectionId: number,
    updateData: UpdateDocumentOutputSubsection
  ): Promise<DocumentOutputSubsection> => {
    const response = await apiRequest<{ data: DocumentOutputSubsection }>(
      `${SUPABASE_URL}/functions/v1/document-output-formats/subsections/${subsectionId}`,
      {
        method: "PUT",
        body: updateData,
      }
    );
    return response.data;
  },

  /**
   * Delete a subsection
   */
  deleteSubsection: async (subsectionId: number): Promise<void> => {
    await apiRequest<{ success: boolean }>(
      `${SUPABASE_URL}/functions/v1/document-output-formats/subsections/${subsectionId}`,
      {
        method: "DELETE",
      }
    );
  },

  /**
   * Create a new field for a subsection
   */
  createField: async (
    subsectionId: number,
    fieldData: CreateDocumentOutputField
  ): Promise<DocumentOutputField> => {
    const response = await apiRequest<{ data: DocumentOutputField }>(
      `${SUPABASE_URL}/functions/v1/document-output-formats/subsections/${subsectionId}`,
      {
        method: "POST",
        body: fieldData,
      }
    );
    return response.data;
  },

  /**
   * Update a field
   */
  updateField: async (
    fieldId: number,
    updateData: UpdateDocumentOutputField
  ): Promise<DocumentOutputField> => {
    const response = await apiRequest<{ data: DocumentOutputField }>(
      `${SUPABASE_URL}/functions/v1/document-output-formats/fields/${fieldId}`,
      {
        method: "PUT",
        body: updateData,
      }
    );
    return response.data;
  },

  /**
   * Delete a field
   */
  deleteField: async (fieldId: number): Promise<void> => {
    await apiRequest<{ success: boolean }>(
      `${SUPABASE_URL}/functions/v1/document-output-formats/fields/${fieldId}`,
      {
        method: "DELETE",
      }
    );
  },

  /**
   * Reorder sections
   */
  reorderSections: async (
    documentId: number,
    sectionIds: number[]
  ): Promise<void> => {
    // Update section orders in parallel
    const updatePromises = sectionIds.map((sectionId, index) =>
      documentOutputFormatApi.updateSection(sectionId, { section_order: index })
    );

    await Promise.all(updatePromises);
  },

  /**
   * Reorder subsections within a section
   */
  reorderSubsections: async (
    sectionId: number,
    subsectionIds: number[]
  ): Promise<void> => {
    // Update subsection orders in parallel
    const updatePromises = subsectionIds.map((subsectionId, index) =>
      documentOutputFormatApi.updateSubsection(subsectionId, {
        subsection_order: index,
      })
    );

    await Promise.all(updatePromises);
  },

  /**
   * Reorder fields within a subsection
   */
  reorderFields: async (
    subsectionId: number,
    fieldIds: number[]
  ): Promise<void> => {
    // Update field orders in parallel
    const updatePromises = fieldIds.map((fieldId, index) =>
      documentOutputFormatApi.updateField(fieldId, { field_order: index })
    );

    await Promise.all(updatePromises);
  },
};
